import { create } from 'zustand';

const TYPING_TTL_MS = 3_000;

type ChatTypingEntry = {
  userId: string;
};

type ChatTypingSlice = {
  typingByChatId: Record<string, ChatTypingEntry>;
  setTyping: (input: { chatId: string; userId: string }) => void;
  clearTyping: (chatId: string) => void;
  clearAllTyping: () => void;
};

const typingTimers = new Map<string, number>();

const clearTypingTimer = (chatId: string) => {
  const timer = typingTimers.get(chatId);
  if (timer === undefined) {
    return;
  }

  if (typeof window !== 'undefined') {
    window.clearTimeout(timer);
  }
  typingTimers.delete(chatId);
};

const removeTypingEntry = (
  typingByChatId: Record<string, ChatTypingEntry>,
  chatId: string
): Record<string, ChatTypingEntry> | null => {
  if (!(chatId in typingByChatId)) {
    return null;
  }

  const next = { ...typingByChatId };
  delete next[chatId];
  return next;
};

export const useChatTypingStore = create<ChatTypingSlice>()((set) => ({
  typingByChatId: {},
  setTyping: ({ chatId, userId }) => {
    if (!chatId || !userId) {
      return;
    }

    clearTypingTimer(chatId);
    if (typeof window !== 'undefined') {
      const timer = window.setTimeout(() => {
        typingTimers.delete(chatId);
        set((state) => {
          const next = removeTypingEntry(state.typingByChatId, chatId);
          if (!next) {
            return {};
          }

          return { typingByChatId: next };
        });
      }, TYPING_TTL_MS);

      typingTimers.set(chatId, timer);
    }

    set((state) => ({
      typingByChatId: {
        ...state.typingByChatId,
        [chatId]: { userId },
      },
    }));
  },
  clearTyping: (chatId) => {
    if (!chatId) {
      return;
    }

    clearTypingTimer(chatId);
    set((state) => {
      const next = removeTypingEntry(state.typingByChatId, chatId);
      if (!next) {
        return {};
      }

      return { typingByChatId: next };
    });
  },
  clearAllTyping: () => {
    if (typeof window !== 'undefined') {
      for (const timer of typingTimers.values()) {
        window.clearTimeout(timer);
      }
    }
    typingTimers.clear();
    set({ typingByChatId: {} });
  },
}));
